import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ChevronDown, X } from "lucide-react";
import Logo from "./Logo";
import AccountMenu from "./AccountMenu";
import { NAV_LINKS } from "../mock";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "./ui/sheet";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-[0_2px_20px_rgba(0,0,0,0.06)]" : "bg-white/95"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between h-[74px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setOpenMenu(link.label)}
                  onMouseLeave={() => setOpenMenu(null)}
                >
                  <button
                    onClick={() => link.path && navigate(link.path)}
                    className={`flex items-center gap-1 text-[14px] font-400 transition-colors duration-200 ${
                      link.path && isActive(link.path) ? "text-[#b8935f]" : "text-[#2b2b2b] hover:text-[#b8935f]"
                    }`}
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${openMenu === link.label ? "rotate-180" : ""}`} />
                  </button>
                  <div
                    className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-200 ${
                      openMenu === link.label ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1"
                    }`}
                  >
                    <div className="bg-white rounded-lg shadow-[0_10px_40px_rgba(0,0,0,0.12)] py-2 w-60 border border-[#f0ece5]">
                      {link.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.path}
                          className="block px-5 py-2.5 text-[14px] text-[#4a4a4a] hover:text-[#b8935f] hover:bg-[#faf7f2] transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  className={`text-[14px] font-400 transition-colors duration-200 relative ${
                    isActive(link.path) ? "text-[#b8935f]" : "text-[#2b2b2b] hover:text-[#b8935f]"
                  }`}
                >
                  {link.label}
                  {isActive(link.path) && (
                    <span className="absolute -bottom-1.5 left-0 w-full h-[1.5px] bg-[#b8935f]" />
                  )}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-4">
            <AccountMenu />
            <button
              onClick={() => navigate("/services#valuation")}
              className="hidden sm:inline-flex items-center px-5 py-2.5 text-[13px] font-500 text-white bg-[#b8935f] rounded-full hover:bg-[#a67f4d] transition-colors duration-200 shadow-sm"
            >
              Book a free Valuation
            </button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 text-[#2b2b2b]" aria-label="Menu">
                  <Menu size={24} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] p-0">
                <div className="flex items-center justify-between px-6 h-[74px] border-b border-[#f0ece5]">
                  <Logo />
                </div>
                <nav className="flex flex-col p-6 gap-1">
                  {NAV_LINKS.map((link) => (
                    <div key={link.label}>
                      {link.path ? (
                        <Link
                          to={link.path}
                          onClick={() => setMobileOpen(false)}
                          className={`block py-3 text-[15px] font-400 ${
                            isActive(link.path) ? "text-[#b8935f]" : "text-[#2b2b2b]"
                          }`}
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <p className="block py-3 text-[13px] font-500 uppercase tracking-wide text-[#a0a0a0]">{link.label}</p>
                      )}
                      {link.children && (
                        <div className="pl-4 flex flex-col gap-1 mb-2">
                          {link.children.map((child) => (
                            <Link
                              key={child.label}
                              to={child.path}
                              onClick={() => setMobileOpen(false)}
                              className="py-1.5 text-[13px] text-[#7a7a7a] hover:text-[#b8935f]"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      navigate("/services#valuation");
                    }}
                    className="mt-4 px-5 py-3 text-[14px] font-500 text-white bg-[#b8935f] rounded-full hover:bg-[#a67f4d] transition-colors"
                  >
                    Book a free Valuation
                  </button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
